import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NurturingHistory from 'app/apiService/nurturingHistory/nurturingHistoryApiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import {
  buildNurturingHistoryShortUrlPayload,
  getNurturingHistoryOpportunityIdFromRow,
} from 'app/pages/OpportunitiesPage/utils/nurturingHistoryUtils';
import { buildTestNurturingBreadcrumbs } from 'app/pages/TestNurturingPage/utils/testNurturingRouteUtils';

function getNurturingHistoryDisplayLabel(
  row: Record<string, unknown> | undefined,
  emailAddress: string,
): string {
  const name = row?.name != null ? String(row.name).trim() : '';
  return name || emailAddress;
}

export function useTestNurturingJourneysGrid(selectedDealerId: string) {
  const dispatch = useDispatch();
  const history = useHistory();

  const handleJourneysCellClick = useCallback(
    async (cell: { field?: string; row?: Record<string, unknown> }) => {
      if (cell?.field !== 'name') {
        return;
      }

      const row = cell.row;
      const opportunityId = getNurturingHistoryOpportunityIdFromRow(row);
      if (!opportunityId || !selectedDealerId) {
        return;
      }

      try {
        const payload = buildNurturingHistoryShortUrlPayload({
          rowDetails: row,
          opportunityId,
          dealerId: selectedDealerId,
          analytics: false,
        });
        const { emailAddress } = payload;
        const displayLabel = getNurturingHistoryDisplayLabel(row, emailAddress);

        const response = await NurturingHistory.createShortUrl(payload);

        if (response.statusCode !== 201 && response.statusCode !== 200) {
          throw new Error(response.message);
        }

        const shortUrl = response.body?.shortUrl;
        if (!shortUrl) {
          throw new Error('Short URL not received from API response');
        }

        const urlId = NurturingHistory.extractUrlId(shortUrl);
        if (!urlId) {
          throw new Error('Could not extract URL ID from short URL');
        }

        const nurturingHistoryBreadcrumbs = [
          ...buildTestNurturingBreadcrumbs('journeys', { isActive: false }),
          {
            label: `Nurturing History (${displayLabel})`,
            link: `/url/${urlId}`,
            isActive: true,
          },
        ];

        history.push(`/url/${urlId}`, {
          breadcrumbsList: nurturingHistoryBreadcrumbs,
          fromPage: 'TestNurturing',
          emailAddress,
          opportunityId,
          dealerId: selectedDealerId,
        });
      } catch (err: unknown) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: err instanceof Error ? err.message : String(err),
          }),
        );
      }
    },
    [dispatch, history, selectedDealerId],
  );

  return { handleJourneysCellClick };
}
